import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { getSupabaseClient } from "../supabaseClient";
import { useBusinessType } from '../context/BusinessContext';
import { motion, AnimatePresence } from "framer-motion";
import { 
  Envelope, 
  Lock, 
  CircleNotch, 
  WarningCircle, 
  SignIn,
  CheckCircle,
  Eye,
  EyeSlash,
} from "@phosphor-icons/react";
import { getOnboardingRedirectPath } from '../components/signup/OnboardingRoutes';
import posthog from 'posthog-js';
import { createPortal } from 'react-dom';

// Enhanced error message component with animation
const ErrorMessage = ({ error }) => {
  return (
    <motion.div
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: "auto" }}
      exit={{ opacity: 0, height: 0 }}
      transition={{ 
        duration: 0.3,
        ease: [0.22, 1, 0.36, 1]
      }}
      className="mb-4 p-3 bg-red-50 border border-red-100 rounded-xl flex items-center gap-2 text-red-600 text-xs sm:text-sm"
    >
      <WarningCircle className="w-4 h-4 sm:w-[15px] sm:h-[15px] flex-shrink-0" />
      <div>{error}</div>
    </motion.div>
  );
};

// Success message component
const SuccessMessage = ({ message }) => {
  return (
    <motion.div
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: "auto" }}
      exit={{ opacity: 0, height: 0 }}
      transition={{ 
        duration: 0.3,
        ease: [0.22, 1, 0.36, 1]
      }}
      className="mb-4 p-3 bg-green-50 border border-green-100 rounded-xl flex items-center gap-2 text-green-600 text-xs sm:text-sm"
    >
      <CheckCircle className="w-4 h-4 sm:w-[15px] sm:h-[15px] flex-shrink-0" />
      <div>{message}</div>
    </motion.div>
  );
};

// Enhanced password input component with premium design
const PasswordInput = ({ value, onChange, loading, variants }) => {
  const [showPassword, setShowPassword] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  return (
    <motion.div 
      className="space-y-2"
      variants={variants} 
      custom={1}
    >
      <motion.label 
        className="block text-sm font-medium text-gray-700"
        animate={{ 
          opacity: isFocused ? 1 : 0.8,
          y: isFocused ? -1 : 0
        }}
        transition={{ duration: 0.2 }}
      >
        Password
      </motion.label>

      <div className="relative">
        <motion.div 
          className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400"
          animate={{ 
            color: isFocused ? "#3B82F6" : "#9CA3AF" 
          }}
          transition={{ duration: 0.2 }}
        >
          <Lock className="w-5 h-5" />
        </motion.div>

        <motion.div 
          className="relative overflow-hidden rounded-xl"
          animate={{ 
            boxShadow: isFocused 
              ? "0 0 0 2px rgba(59,130,246,0.2), 0 4px 16px rgba(59,130,246,0.1)" 
              : "0 2px 10px rgb(0,0,0,0.04)"
          }}
          transition={{ duration: 0.3 }}
        >
          <input
            type={showPassword ? "text" : "password"}
            value={value}
            onChange={onChange}
            required
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            className="w-full pl-12 pr-14 py-3 bg-white border border-gray-100 rounded-xl focus:ring-0 focus:outline-none transition-all duration-300 text-sm placeholder:text-gray-400 font-light"
            placeholder="Enter your password"
            disabled={loading}
          />
        </motion.div>

        <div className="absolute right-4 top-1/2 transform -translate-y-1/2">
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            type="button"
            className="text-gray-400 hover:text-gray-700 rounded-full h-8 w-8 flex items-center justify-center transition-colors duration-200"
            onClick={() => setShowPassword(!showPassword)}
          >
            {showPassword ? 
              <EyeSlash className="w-4 h-4" /> : 
              <Eye className="w-4 h-4" />
            }
          </motion.button>
        </div>
      </div>
    </motion.div>
  );
};

// Enhanced email input component with premium design
const EmailInput = ({ value, onChange, loading, variants }) => {
  const [isFocused, setIsFocused] = useState(false);

  return (
    <motion.div 
      className="space-y-2"
      variants={variants}
      custom={0}
    >
      <motion.label 
        className="block text-sm font-medium text-gray-700"
        animate={{ 
          opacity: isFocused ? 1 : 0.8,
          y: isFocused ? -1 : 0
        }}
        transition={{ duration: 0.2 }}
      >
        Email
      </motion.label>

      <div className="relative">
        <motion.div 
          className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400"
          animate={{ 
            color: isFocused ? "#3B82F6" : "#9CA3AF" 
          }}
          transition={{ duration: 0.2 }}
        >
          <Envelope className="w-5 h-5" />
        </motion.div>

        <motion.div 
          className="relative overflow-hidden rounded-xl"
          animate={{ 
            boxShadow: isFocused 
              ? "0 0 0 2px rgba(59,130,246,0.2), 0 4px 16px rgba(59,130,246,0.1)" 
              : "0 2px 10px rgb(0,0,0,0.04)"
          }}
          transition={{ duration: 0.3 }}
        >
          <input
            type="email"
            value={value}
            onChange={onChange}
            required
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            className="w-full pl-12 pr-4 py-3 bg-white border border-gray-100 rounded-xl focus:ring-0 focus:outline-none transition-all duration-300 text-sm placeholder:text-gray-400 font-light"
            placeholder="Enter your email"
            disabled={loading}
          />
        </motion.div>
      </div>
    </motion.div>
  );
};

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const navigate = useNavigate();
  const { businessType, setBusinessType } = useBusinessType();
  const supabase = getSupabaseClient(); // Use unified client

  // Check for return URL (e.g., from invitation links)
  const urlParams = new URLSearchParams(window.location.search);
  const returnUrl = urlParams.get('returnUrl');

  // Enhanced animation variants
  const backdropVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { duration: 0.3 }
    },
    exit: { 
      opacity: 0,
      transition: { duration: 0.2 }
    }
  };

  const popupVariants = {
    hidden: { scale: 0.95, opacity: 0, y: 20 },
    visible: { 
      scale: 1, 
      opacity: 1, 
      y: 0,
      transition: { 
        type: "spring", 
        damping: 30, 
        stiffness: 300,
        duration: 0.4 
      }
    },
    exit: { 
      scale: 0.98, 
      opacity: 0,
      y: 10,
      transition: { duration: 0.2 }
    }
  };

  const formGroupVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: (i) => ({ 
      opacity: 1, 
      y: 0,
      transition: { 
        delay: i * 0.05,
        duration: 0.3,
        ease: [0.22, 1, 0.36, 1]
      }
    })
  };

  const handleOnboardingRedirect = (onboardingStatus) => {
    switch (onboardingStatus) {
      case 'not_started':
        return '/onboarding';
      case 'number_selection':
        return '/onboarding/phone';
      case 'pathway_setup':
        return '/onboarding/pathway';
      case 'business_details':
        return '/onboarding/details';
      case 'completed':
        return '/home';
      default:
        return '/onboarding';
    }
  };

  const tryLoginWithClient = async () => {
    try {
      // Use the unified client instead of business-type specific client
      const client = getSupabaseClient();

      const { data, error } = await client.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      if (data?.user) {
        // Look up account via account_users table for multi-user support
        const { data: userAccount, error: userAccountError } = await client
          .from("user_accounts")
          .select("*")
          .eq("user_id", data.user.id)
          .eq("is_active", true)
          .limit(1)
          .single();

        if (userAccountError) {
          // Fallback to direct account lookup for backward compatibility
          const { data: directAccount, error: directAccountError } = await client
            .from("accounts")
            .select("*")
            .eq("id", data.user.id)
            .limit(1)
            .single();

          if (directAccountError) throw new Error('Account not found');

          // Return direct account with simulated user_accounts structure
          const account = {
            ...directAccount,
            user_id: data.user.id,
            account_id: directAccount.id,
            role: 'owner',
            permissions: { can_view: true, can_edit: true, can_admin: true },
            account_email: directAccount.email,
            account_name: directAccount.full_name,
            account_created_at: directAccount.created_at
          };

          return { success: true, data, account };
        }

        if (userAccount) {
          return { success: true, data, account: userAccount };
        }
      }

      return { success: false, error: new Error('Account not found') };
    } catch (error) {
      return { success: false, error };
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      let loginResult = await tryLoginWithClient();
      if (!loginResult.success) {
        throw new Error("Invalid email or password");
      }

      const { data, account } = loginResult;
      const businessType = account.business_type?.toLowerCase();

      // Add PostHog user identification
      posthog.identify(
        data.user.id,
        { 
          email: data.user.email,
          name: account.account_name || account.full_name,
          business_type: account.business_type,
          account_id: account.account_id,
          user_role: account.role,
          $set: {
            last_login: new Date().toISOString()
          }
        }
      );

      // Check if user is a team member (skip onboarding for team members)
      const isTeamMember = data.user.id !== account.account_id;

      if (isTeamMember) {
        // Team members skip onboarding entirely
        setBusinessType(businessType);
        localStorage.setItem("businessType", businessType);

        // If there's a return URL (like from invitation), use that
        if (returnUrl) {
          console.log('🔄 Team member redirecting to return URL:', returnUrl);
          navigate(returnUrl);
          return;
        }

        // Otherwise go to home dashboard
        navigate('/home');
        return;
      }

      // Only check onboarding status for account owners (user.id == account.account_id)
      const { data: onboardingData, error: onboardingError } = await supabase
        .from('onboarding_data')
        .select('current_step')
        .eq('account_id', account.account_id)
        .limit(1)
        .single();

      if (onboardingError) throw onboardingError;

      // Handle first-time login
      if (onboardingData?.current_step === 'not_started') {
        const { error: updateError } = await supabase
          .from('onboarding_data')
          .update({ current_step: 'intro' })
          .eq('account_id', account.account_id);

        if (updateError) throw updateError;

        setBusinessType(businessType);
        localStorage.setItem("businessType", businessType);

        // If there's a return URL (like from invitation), use that instead of onboarding
        if (returnUrl) {
          console.log('🔄 First-time login redirecting to return URL:', returnUrl);
          navigate(returnUrl);
          return;
        }

        navigate('/onboarding/intro');
        return;
      }

      // For returning users, redirect based on their current step
      setBusinessType(businessType);
      localStorage.setItem("businessType", businessType);

      // If there's a return URL (like from invitation), use that
      if (returnUrl) {
        console.log('🔄 Redirecting to return URL:', returnUrl);
        navigate(returnUrl);
        return;
      }

      const redirectPath = getOnboardingRedirectPath(onboardingData?.current_step);

      // Check calls count if user would normally go to /home
      if (redirectPath === '/home') {
        const { count, error: callsError } = await supabase
          .from('calls')
          .select('*', { count: 'exact', head: true })
          .eq('account_id', account.account_id);

        if (callsError) throw callsError;

        // Redirect to brain if less than 100 calls
        if (count < 100) {
          navigate('/train/brainv2');
          return;
        }
      }

      navigate(redirectPath);

    } catch (error) {
      console.error("Login error:", error);
      setError(
        error.message === "Invalid login credentials" || 
        error.message === "Invalid email or password"
          ? "Invalid email or password"
          : "An error occurred during login. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!email) {
      setError("Please enter your email address");
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (error) throw error;
      setResetSent(true);
    } catch (err) {
      console.error("Reset password error:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    try {
      setLoading(true);
      setError(null);
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/home`,
        }
      });
      if (error) throw error;
      // The OAuth redirect will handle the rest
    } catch (err) {
      console.error("Google login error:", err);
      setError(err.message);
      setLoading(false);
    }
  };

  // Add a logout handler that resets PostHog
  const handleLogout = async () => {
    await supabase.auth.signOut();
    posthog.reset(); // Reset PostHog tracking on logout
    navigate('/login');
  };

  return (
    <motion.div 
      className="min-h-screen w-full bg-gradient-to-br from-gray-50/80 via-white to-blue-50/30 backdrop-blur-[80px] overflow-auto py-4 sm:py-6"
      initial="hidden"
      animate="visible"
      exit="exit"
      variants={backdropVariants}
    >
      <motion.div 
        className="w-full max-w-lg mx-auto px-4"
        variants={popupVariants}
      >
        {/* Streamlined Header */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.3 }}
          className="relative flex items-center justify-center mb-6"
        >
          {/* Subtle background glow */}
          <div className="absolute inset-0 bg-gradient-to-br from-blue-50/30 via-transparent to-indigo-50/20 rounded-3xl blur-3xl"></div>

          <div className="relative flex items-center flex-col text-center py-3">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 tracking-tight mb-1">
              Welcome back
            </h1>
            <p className="text-gray-600 font-light text-sm">
              Log in to access your AI assistant dashboard
            </p>
          </div>
        </motion.div>

        {/* Premium Form Container */}
        <motion.div 
          className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-2xl border border-gray-200/50 overflow-hidden"
          variants={popupVariants}
        >
          <div className="p-6 sm:p-8">
            <AnimatePresence initial={false} mode="wait">
              {error && <ErrorMessage error={error} />}
              {resetSent && <SuccessMessage message="Password reset instructions have been sent to your email" />}
            </AnimatePresence>

            <form onSubmit={handleSubmit}>
              <motion.div 
                className="space-y-5"
                initial="hidden"
                animate="visible"
                variants={{
                  hidden: {},
                  visible: {
                    transition: {
                      staggerChildren: 0.08
                    }
                  }
                }}
              >
                <EmailInput 
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  loading={loading}
                  variants={formGroupVariants}
                />

                <PasswordInput 
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  loading={loading}
                  variants={formGroupVariants}
                />

                <motion.div 
                  className="flex justify-end"
                  variants={formGroupVariants}
                  custom={2}
                >
                  <button
                    type="button"
                    onClick={handleResetPassword}
                    className="text-sm text-gray-600 hover:text-gray-900 transition-colors duration-200 font-medium hover:underline"
                  >
                    Forgot password?
                  </button>
                </motion.div>

                {/* Premium Login Button */}
                <motion.button
                  type="submit"
                  disabled={loading}
                  whileHover={{ scale: 1.02, y: -1 }}
                  whileTap={{ scale: 0.98 }}
                  variants={formGroupVariants}
                  custom={3}
                  className="group w-full bg-gradient-to-r from-gray-900 to-black hover:from-black hover:to-gray-900 text-white text-sm py-3 px-6 rounded-xl focus:outline-none transition-all duration-300 flex items-center justify-center gap-3 font-medium shadow-[0_4px_16px_rgba(0,0,0,0.2)] hover:shadow-[0_8px_24px_rgba(0,0,0,0.3)] relative overflow-hidden"
                >
                  {/* Subtle shine effect */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000"></div>

                  {loading ? (
                    <div className="flex items-center">
                      <CircleNotch className="mr-3 h-5 w-5 animate-spin" />
                      Logging in...
                    </div>
                  ) : (
                    <div className="flex items-center">
                      Log in
                      <SignIn className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform duration-300" />
                    </div>
                  )}
                </motion.button>
              </motion.div>
            </form>

            {/* Premium Google Login Button */}
            <motion.div 
              className="mt-5"
              variants={formGroupVariants}
              custom={4}
              initial="hidden"
              animate="visible"
            >
              <motion.button
                type="button"
                onClick={handleGoogleLogin}
                disabled={loading}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="w-full bg-white text-gray-700 border border-gray-200 hover:border-gray-300 text-sm py-3 px-6 rounded-xl focus:outline-none transition-all duration-300 flex items-center justify-center gap-3 font-medium shadow-sm hover:shadow-md"
              >
                <img
                  src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
                  alt="Google"
                  className="w-5 h-5" 
                />
                Sign in with Google
              </motion.button>
            </motion.div>
          </div>

          {/* Elegant Footer */}
          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="px-6 sm:px-8 py-4 bg-gray-50/80 backdrop-blur-sm flex justify-center gap-2 sm:gap-3 border-t border-gray-200/50"
          >
            <motion.p className="text-sm text-gray-600 font-light">
              Don't have an account?{" "}
              <Link
                to="/signup"
                className="text-gray-900 font-medium hover:underline transition-colors"
              >
                Sign up
              </Link>
            </motion.p>
          </motion.div>
        </motion.div>
      </motion.div>
    </motion.div>
  );
};

export default Login;